import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { toast } from 'react-toastify';

const UNIT_OPTIONS = [
  { value: 'Each', label: 'Each' },
  { value: 'gram', label: 'gram' },
  { value: 'kilogram', label: 'kilogram' },
  { value: 'liters', label: 'liters' },
];

export default function CalculatedFoodDialog({ open, onClose, mainItem, onSaved }) {
  const [itemOptions, setItemOptions] = useState([]);
  const [rows, setRows] = useState([{ ingredientItemId: null, amount: '', unitType: 'Each' }]);
  const [loading, setLoading] = useState(false);

  const ingredientOptions = useMemo(() => {
    if (!mainItem?.ItemID) return itemOptions;
    return itemOptions.filter((i) => i.ItemID !== mainItem.ItemID);
  }, [itemOptions, mainItem?.ItemID]);

  const resetState = () => {
    setRows([{ ingredientItemId: null, amount: '', unitType: 'Each' }]);
    setItemOptions([]);
    setLoading(false);
  };

  useEffect(() => {
    if (!open || !mainItem?.ItemID) return;

    setLoading(true);
    Promise.all([
      axios.get('https://serverrion.mylksystems.store/Item/getItemDetails'),
      axios.get(`https://serverrion.mylksystems.store/calculatedfood/main/${mainItem.ItemID}`),
    ])
      .then(([itemsRes, recipeRes]) => {
        const items = Array.isArray(itemsRes.data) ? itemsRes.data : [];
        setItemOptions(items);

        const recipe = Array.isArray(recipeRes.data) ? recipeRes.data : [];
        if (recipe.length > 0) {
          setRows(
            recipe.map((r) => ({
              ingredientItemId: r.ingredientItemId,
              amount: String(r.amount ?? ''),
              unitType: r.unitType || 'Each',
            }))
          );
        } else {
          setRows([{ ingredientItemId: null, amount: '', unitType: 'Each' }]);
        }
      })
      .catch(() => {
        toast.error('Failed to load ingredients.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, mainItem?.ItemID]);

  const closeDialog = () => {
    onClose?.();
    resetState();
  };

  const addRow = () => {
    setRows((prev) => [...prev, { ingredientItemId: null, amount: '', unitType: 'Each' }]);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleSave = async () => {
    if (!mainItem?.ItemID) return;

    const cleaned = rows
      .map((r) => ({
        ingredientItemId: r.ingredientItemId,
        amount: Number(r.amount),
        unit: r.unitType,
      }))
      .filter((r) => r.ingredientItemId);

    if (cleaned.length === 0) {
      toast.error('Add at least one ingredient.');
      return;
    }
    if (cleaned.some((r) => !Number.isFinite(r.amount) || r.amount <= 0)) {
      toast.error('Please enter a valid quantity for all ingredients.');
      return;
    }

    setLoading(true);
    try {
      await axios.post('https://serverrion.mylksystems.store/calculatedfood/save', {
        mainItemId: mainItem.ItemID,
        ingredients: cleaned,
      });
      toast.success('Ingredients saved.');
      onSaved?.();
      closeDialog();
    } catch (e) {
      toast.error('Failed to save ingredients.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Typography sx={{ fontWeight: 'bold' }}>Calculated Food</Typography>
          <Typography sx={{ fontSize: 14, color: 'gray' }}>
            Main Item: {mainItem?.ItemName} (ID: {mainItem?.ItemID})
          </Typography>
        </div>
        <IconButton onClick={closeDialog}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {rows.map((row, index) => {
          const value =
            ingredientOptions.find((o) => o.ItemID === row.ingredientItemId) || null;

          return (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={ingredientOptions}
                  getOptionLabel={(option) => `${option.ItemName} (${option.ItemID})`}
                  value={value}
                  onChange={(event, newValue) => {
                    updateRow(index, { ingredientItemId: newValue?.ItemID ?? null });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Ingredient" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Qty"
                  type="number"
                  value={row.amount}
                  onChange={(e) => updateRow(index, { amount: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  label="Unit"
                  value={row.unitType}
                  onChange={(e) => updateRow(index, { unitType: e.target.value })}
                  fullWidth
                >
                  {UNIT_OPTIONS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Remove">
                  <IconButton onClick={() => removeRow(index)} disabled={rows.length <= 1}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addRow}
          sx={{ mt: 1 }}
        >
          Add Ingredient
        </Button>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={closeDialog} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
